import { MatrixClient, MatrixError } from 'matrix-js-sdk';

export enum InviteState {
    Invited = 'invited',
    Error = 'error',
}

interface InviteError {
    errorText: string;
    errcode: string;
}

export type CompletionStates = Record<string, InviteState>;

const MAX_INVITE_ATTEMPTS = 3;
const DEFAULT_RATE_LIMIT_RETRY_MS = 5000;

export class MultiInviter {
    private canceled = false;
    private userIds: string[] = [];
    private completionStates: CompletionStates = {};
    private errors: Record<string, InviteError> = {};
    private pendingTimers = new Set<ReturnType<typeof setTimeout>>();
    private deferred: {
        resolve: (value: CompletionStates) => void;
        reject: (reason?: any) => void;
        promise: Promise<CompletionStates>;
    } | null = null;

    constructor(
        private readonly client: MatrixClient,
        private roomId: string,
        private readonly progressCallback?: (completed: number, total: number) => void,
    ) {}

    public invite(userIds: string[]): Promise<CompletionStates> {
        if (this.userIds.length > 0) {
            throw new Error('Already inviting/invited');
        }

        const self = this.client.getUserId();
        this.userIds = [...new Set(userIds)].filter((id) => !!id && id !== self);

        let resolvePromise: (value: CompletionStates) => void;
        let rejectPromise: (reason?: any) => void;
        const promise = new Promise<CompletionStates>((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
        });

        this.deferred = {
            resolve: resolvePromise!,
            reject: rejectPromise!,
            promise,
        };

        this.inviteMore(0);

        return this.deferred.promise;
    }

    public cancel(): void {
        this.canceled = true;
        this.pendingTimers.forEach((timer) => clearTimeout(timer));
        this.pendingTimers.clear();
        this.deferred?.reject(new Error('canceled'));
    }

    public getErrorText(userId: string): string | null {
        return this.errors[userId]?.errorText ?? null;
    }

    private async doInvite(userId: string, attempt = 1): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.client
                .invite(this.roomId, userId)
                .then(() => {
                    if (this.canceled) return;

                    this.completionStates[userId] = InviteState.Invited;
                    delete this.errors[userId];

                    resolve();
                })
                .catch((err) => {
                    if (this.canceled) return;

                    console.error(`Failed to invite ${userId}:`, err);

                    const errcode = err instanceof MatrixError ? err.errcode : 'UNKNOWN';

                    if (errcode === 'M_LIMIT_EXCEEDED' && attempt < MAX_INVITE_ATTEMPTS) {
                        const retryAfterMs = err.data?.retry_after_ms || DEFAULT_RATE_LIMIT_RETRY_MS;
                        console.log(`Rate limited, retrying ${userId} after ${retryAfterMs}ms (attempt ${attempt + 1}/${MAX_INVITE_ATTEMPTS})`);

                        const timer = setTimeout(() => {
                            this.pendingTimers.delete(timer);
                            this.doInvite(userId, attempt + 1).then(resolve, reject);
                        }, retryAfterMs);
                        this.pendingTimers.add(timer);
                        return;
                    }

                    let errorText: string;
                    switch (errcode) {
                        case 'M_LIMIT_EXCEEDED':
                            errorText = 'Rate limited; gave up after retries';
                            break;
                        case 'M_FORBIDDEN':
                            errorText = 'No permission to invite';
                            break;
                        case 'M_NOT_FOUND':
                            errorText = 'User not found';
                            break;
                        default:
                            errorText = err?.message || 'Unknown error';
                    }

                    this.completionStates[userId] = InviteState.Error;
                    this.errors[userId] = { errorText, errcode: errcode || 'UNKNOWN_ERROR' };

                    resolve();
                });
        });
    }

    private inviteMore(nextIndex: number): void {
        if (this.canceled) return;

        if (nextIndex === this.userIds.length) {
            this.deferred?.resolve(this.completionStates);
            return;
        }

        const userId = this.userIds[nextIndex];

        if (this.completionStates[userId] === InviteState.Invited) {
            this.progressCallback?.(nextIndex + 1, this.userIds.length);
            this.inviteMore(nextIndex + 1);
            return;
        }

        this.doInvite(userId)
            .then(() => {
                this.progressCallback?.(nextIndex + 1, this.userIds.length);
                this.inviteMore(nextIndex + 1);
            })
            .catch(() => this.deferred?.resolve(this.completionStates));
    }
}
